import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

import { useHistory } from "react-router";
import noop from "lodash/noop";

import { consoleErrorWithSentry } from "../../utils";
import mixpanel from "../../utils/mixpanel";
import { useAvaCaseIQContext } from "./AvaCaseIQProvider";

export enum AvaOpeningSource {
  FLOATING_BUTTON = "ava.floating_button",
  NAV = "ava.console.icon",
  SUPPORT = "ava.support.button",
  DASHBOARDS = "ava.dashboards.button",
  PLATFORM_DROPDOWN = "ava.support.platform_dropdown",
  URL = "ava.url",
}

type AvaDialogContextType = {
  isAvaDialogOpen: boolean;
  handleOpenAvaDialog: (source: AvaOpeningSource, dashboardId?: string) => void;
  handleCloseAvaDialog: () => void;
  dashboardId: string;
  isFullscreenMode: boolean;
  changeFullscreenMode: (isFullscreenMode: boolean) => void;
  setDashboardId: (dashboardId: string) => void;
  setConversationHasOccurred: (conversationHasOccurred: boolean) => void;
  isAvaDialogMinimized: boolean;
  setIsAvaDialogMinimized: (isAvaDialogMinimized: boolean) => void;
  width: string;
  height: string;
  setWidth: (width: string) => void;
  setHeight: (height: string) => void;
  openCaseIQWithEmptyState: () => void;
};

type Props = {
  children: ReactNode;
};

export const AvaDialogContext = createContext<AvaDialogContextType>({
  isAvaDialogOpen: false,
  handleOpenAvaDialog: noop,
  handleCloseAvaDialog: noop,
  dashboardId: "",
  isFullscreenMode: false,
  changeFullscreenMode: noop,
  setDashboardId: noop,
  setConversationHasOccurred: noop,
  isAvaDialogMinimized: false,
  setIsAvaDialogMinimized: noop,
  width: "550px",
  height: "780px",
  setWidth: noop,
  setHeight: noop,
  openCaseIQWithEmptyState: noop,
});

const removeOpenCaseIQFromUrl = () => {
  const url = new URL(window.location.href);
  if (url.searchParams.has("open-case-iq")) {
    url.searchParams.delete("open-case-iq");
    return `${url.pathname}${url.search}`;
  }
  return window.location.href;
};

export const AvaDialogContextProvider = ({ children }: Props) => {
  const [conversationHasOccurred, setConversationHasOccurred] = useState<boolean>(false);
  const [isAvaDialogOpen, setIsAvaDialogOpen] = useState(false);
  const [dashboardId, setDashboardId] = useState("");
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  const [isAvaDialogMinimized, setIsAvaDialogMinimized] = useState(false);
  const [width, setWidth] = useState(window.innerWidth < 550 ? "90vw" : "550px");
  const [height, setHeight] = useState("780px");

  const { setCaseIQConversationId, setIsCaseIQOpen } = useAvaCaseIQContext();
  const history = useHistory();

  const handleOpenAvaDialog = useCallback(
    (source: AvaOpeningSource, dashboardId?: string) => {
      try {
        mixpanel.track(source);
      } catch (error) {
        consoleErrorWithSentry(error);
      }

      setIsAvaDialogOpen(true);
      setIsAvaDialogMinimized(false);
      setDashboardId(dashboardId ?? "");
      if (isFullscreenMode) {
        document.body.style.overflow = "hidden";
      }
    },
    [setIsAvaDialogOpen, setIsAvaDialogMinimized, setDashboardId, isFullscreenMode]
  );

  const handleMinimizeAvaDialog = useCallback(
    (minimized: boolean) => {
      if (minimized) {
        const url = new URL(window.location.href);
        if (isFullscreenMode) {
          document.body.style.overflow = "auto";
        }

        if (url.searchParams.has("open-case-iq")) {
          history.replace(removeOpenCaseIQFromUrl());
        }
      }
      setIsAvaDialogMinimized(minimized);
    },
    [history, isFullscreenMode]
  );

  const handleCloseAvaDialog = useCallback(() => {
    if (conversationHasOccurred) {
      try {
        mixpanel.track("ava.console.abort");
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    }
    document.body.style.overflow = "auto";
    setIsFullscreenMode(false);
    setIsAvaDialogOpen(false);
    setCaseIQConversationId("");

    const url = new URL(window.location.href);

    if (url.searchParams.has("open-case-iq")) {
      history.replace(removeOpenCaseIQFromUrl());
    }
  }, [setCaseIQConversationId, conversationHasOccurred, history]);

  const changeFullscreenMode = useCallback(
    (isFullscreenMode: boolean) => {
      if (isFullscreenMode) {
        setHeight("98vh");
        setWidth("98vw");
        document.body.style.overflow = "hidden";
      } else {
        setHeight("780px");
        setWidth(window.innerWidth < 550 ? "90vw" : "550px");
        document.body.style.overflow = "auto";
      }

      setIsFullscreenMode(isFullscreenMode);
    },
    [setHeight, setWidth]
  );

  const openCaseIQWithEmptyState = useCallback(() => {
    handleOpenAvaDialog(AvaOpeningSource.FLOATING_BUTTON);
    setCaseIQConversationId("");
    changeFullscreenMode(true);
    setHeight("98vh");
    setWidth("98vw");
    setIsCaseIQOpen(true);
    history.replace(`${window.location.pathname}?open-case-iq=true`);
  }, [handleOpenAvaDialog, setCaseIQConversationId, changeFullscreenMode, setIsCaseIQOpen, history]);

  return (
    <AvaDialogContext.Provider
      value={{
        isAvaDialogOpen,
        handleOpenAvaDialog,
        handleCloseAvaDialog,
        dashboardId,
        isFullscreenMode,
        changeFullscreenMode,
        setDashboardId,
        setConversationHasOccurred,
        isAvaDialogMinimized,
        setIsAvaDialogMinimized: handleMinimizeAvaDialog,
        width,
        height,
        setWidth,
        setHeight,
        openCaseIQWithEmptyState,
      }}
    >
      {children}
    </AvaDialogContext.Provider>
  );
};

export const useAvaDialogContext = () => useContext(AvaDialogContext);
